import CheckmarkIcon from '@assets/icons/checkmark-icon.svg';
import i18n from '@/utils/i18n';
import PendingIcon from '@assets/icons/pending-icon.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import { formatDate } from '@/utils/date';
import { PaymentLink } from './payment-links-list';
import { PaymentsListItem } from './payments-list-item';
import { StyleProp, View, ViewStyle } from 'react-native';
import { Typography, TypographyVariant } from '@usepepper/ui/atoms/typography';

interface Props {
  bottomLeftTypography?: TypographyVariant;
  bottomRightTypography?: TypographyVariant;
  containerStyle?: StyleProp<ViewStyle>;
  currencyCode: string | null;
  isSelected?: boolean;
  onPress: () => void;
  paymentLink: PaymentLink;
  topLeftTypography?: TypographyVariant;
  topRightTypography?: TypographyVariant;
}

export const PaymentLinksListItem: React.FC<Props> = ({
  bottomLeftTypography = 'paragraph-md',
  bottomRightTypography = 'paragraph-md',
  containerStyle,
  currencyCode,
  isSelected,
  onPress,
  paymentLink,
  topLeftTypography = 'paragraph-base-600',
  topRightTypography = 'paragraph-base',
}) => {
  const [styles, theme] = useStyles();

  const isPending = paymentLink.total_remaining_balance_in_micros > 0;

  const bottomRightTextInvoices =
    paymentLink.invoices_count > 1
      ? `${paymentLink.invoices_count} ${i18n.t('invoices')}`
      : `${paymentLink.invoices_count} ${i18n.t('invoice')}`;
  const bottomRightTextCredit =
    paymentLink.credit_memos_count > 0
      ? paymentLink.credit_memos_count > 1
        ? `, ${paymentLink.credit_memos_count} ${i18n.t('credits')}`
        : `, ${paymentLink.credit_memos_count} ${i18n.t('credit')}`
      : '';
  const bottomRightText = `${bottomRightTextInvoices}${bottomRightTextCredit}`;

  return (
    <PaymentsListItem
      bottomLeft={
        <View style={styles.statusRow}>
          {isPending ? <PendingIcon /> : <CheckmarkIcon />}
          <Typography
            color={theme.sk.color.text.tertiary}
            variant={bottomLeftTypography}
          >
            {isPending ? i18n.t('pending') : i18n.t('paid')}
          </Typography>
        </View>
      }
      bottomRight={
        <Typography
          color={theme.sk.color.text.tertiary}
          variant={bottomRightTypography}
        >
          {bottomRightText}
        </Typography>
      }
      containerStyle={containerStyle}
      isSelected={isSelected}
      onPress={onPress}
      testID="payment-links-list-item"
      topLeft={
        <Typography
          color={theme.sk.color.text.primary}
          variant={topLeftTypography}
        >
          {i18n.t('created_on', {
            date: formatDate(new Date(paymentLink.payment_link?.created_at!)),
          })}
        </Typography>
      }
      topRight={
        <Typography
          color={theme.sk.color.text.primary}
          variant={topRightTypography}
        >
          {formatCurrency(
            paymentLink.total_remaining_balance_in_micros >= 0
              ? paymentLink.total_remaining_balance_in_micros
              : 0,
            currencyCode,
          )}
        </Typography>
      }
    />
  );
};

const useStyles = createStyles((theme) => ({
  statusRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
